import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BackPill } from './BackPill';
import { ChevronLeft } from 'lucide-react';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { getAudioCtx } from '../../utils/audioCore';
import { vibrate as nativeVibrate } from '../../utils/haptics';
import { useAuth } from '../../contexts/AuthContext';
import { hiResAvatar } from '../../utils/avatar';

/* ═══════════════════════════════════════════════════════════════════
   THE RELIEF — 87s Imperial Reset · Brain-Relief Palette
   Phase 1  Preparatory Breath  (0-42s)   → 7 cycles, indigo pulse, low sine
   Phase 2  Purge Input         (42-45s+) → textarea, indigo aura, user "Energy Out"
   Phase 3  Release Breath      (0-42s)   → 7 cycles, indigo orb, harmonic chime
   Phase 4  Spectrum Burst      (3s)      → teal/lavender/peach + affirmation
   Gold 1px border retained throughout for brand identity.
   0-cost · 0-storage · procedural (WebAudio + CSS/SVG only)
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#C5A059';
const GOLD_BRIGHT = '#E9C77A';
const GOLD_SOFT = '#8B6B2F';
const BG_CALM = '#050505';
/* Brain-Relief palette */
const INDIGO = '#4B0082';
const INDIGO_DEEP = '#2A004A';
const INDIGO_GLOW = '#7B3FBF';
const TEAL = '#48D1CC';
const LAVENDER = '#E6E6FA';
const PEACH = '#FFDAB9';
/* Multi-colour burst palette — pillar icon colours + relief spectrum + gold */
const BURST_PALETTE = ['#FFD86B', '#FF4A6A', '#4A85E8', '#4FE8B4', '#B08CFF', '#5BC0F0', '#F5A623', TEAL, LAVENDER, PEACH, '#FF7AD9', '#FFFFFF'];

/* Phase III spectrum — one colour per breath half (7 cycles × 2 = 14 steps):
   purple → the 8 pillar icon colours → back to purple, then the burst. */
const SPECTRUM = [
  '#7B3FBF', '#7B3FBF',
  '#D4AF37', '#C8A95E', '#0F52BA', '#DC143C', '#10B981', '#E5C44D', '#8B5CF6', '#2A9BD8',
  '#536DCB', '#7B3FBF', '#7B3FBF', '#7B3FBF',
];
const SPECTRUM_NOTES = [262, 294, 330, 392, 440, 523, 587, 659, 784, 880, 784, 659, 587, 523];
/* After the burst: big profile ring — empty dark → white energy fills
   bottom→top → golden energy fills bottom→top → page brightens. */
const ReliefCrest = ({ user }) => {
  const photo = hiResAvatar(user?.profile_picture || null);
  const initial = (user?.nickname || user?.email || 'B')[0].toUpperCase();
  return (
    <div className="relative flex-shrink-0 rounded-full" style={{ width: 256, height: 256 }} data-testid="relief-crest">
      <div className="absolute inset-0 rounded-full rc-glow" style={{ background: '#15151c' }}/>
      <div className="absolute inset-0 rounded-full rc-fill-white"
        style={{ background: 'linear-gradient(0deg, #FFFFFF, #E6ECFF 60%, #FFFFFF)' }}/>
      <div className="absolute inset-0 rounded-full rc-fill-gold"
        style={{ background: `linear-gradient(0deg, ${GOLD_SOFT}, ${GOLD} 35%, ${GOLD_BRIGHT} 70%, #FFF1B8)` }}/>
      <div className="absolute rounded-full rc-aura overflow-hidden flex items-center justify-center"
        style={{ inset: 22, background: '#0a0806', border: '2px solid #000' }} data-testid="relief-crest-photo">
        {photo
          ? <img src={photo} alt="" className="w-full h-full object-cover"/>
          : <span className="font-['Cinzel']" style={{ color: GOLD_BRIGHT, fontSize: 64 }}>{initial}</span>}
      </div>
    </div>
  );
};

/* Breath cadence — 7 cycles × 6 s (3 s in / 3 s out) = 42 s per phase. */
const CYCLES_PER_PHASE = 7;
const BREATH_IN_MS = 3000;
const BREATH_OUT_MS = 3000;
const BREATH_CYCLE_MS = BREATH_IN_MS + BREATH_OUT_MS;         // 6000
const BREATH_PHASE_MS = BREATH_CYCLE_MS * CYCLES_PER_PHASE;   // 42000
/* 7 back-to-back bursts: centre, top, bottom, right, left, centre, everywhere */
const BURST_ORIGINS = [[50, 45], [50, 18], [50, 80], [82, 48], [18, 48], [30, 28], [70, 66]];
const BURST_GAP_MS = 1100;
const ROCKET_MS = 420;
const BURST_PARTICLES = 90;
/* One transformation per burst — the negative is blown away, the positive rises. */
const BURST_WORDS = [
  ['Fear is burst', 'Courage rises'], ['Heaviness is gone', 'Lightness flows'],
  ['Doubt breaks', 'Faith awakens'], ['Pain is gone', 'Power flows'],
  ['Chaos fades', 'Calm returns'], ['Low is burst', 'You rise high'],
  ['Darkness breaks', 'Radiance shines'],
];

const AFFIRMATION = 'Now come on, the world is waiting for you.';

/* ────────────── Audio · continuous synth per phase ────────────── */
const useBreathAudio = () => {
  const ctxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);

  const start = (frequency = 110) => {
    try {
      stop();
      const ctx = getAudioCtx();
      if (!ctx) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.06, ctx.currentTime + 0.3);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      ctxRef.current = ctx;
      oscRef.current = osc;
      gainRef.current = gain;
    } catch { /* noop */ }
  };

  const pulseAmp = (up) => {
    if (!gainRef.current || !ctxRef.current) return;
    try {
      const t = ctxRef.current.currentTime;
      gainRef.current.gain.cancelScheduledValues(t);
      gainRef.current.gain.setTargetAtTime(up ? 0.09 : 0.04, t, 0.6);
    } catch { /* noop */ }
  };

  const rampFrequency = (target, seconds = 3) => {
    if (!oscRef.current || !ctxRef.current) return;
    try {
      const t = ctxRef.current.currentTime;
      oscRef.current.frequency.cancelScheduledValues(t);
      oscRef.current.frequency.linearRampToValueAtTime(target, t + seconds);
    } catch { /* noop */ }
  };

  const stop = () => {
    try {
      const gain = gainRef.current;
      const ctx = ctxRef.current;
      const osc = oscRef.current;
      if (gain && ctx) {
        const t = ctx.currentTime;
        gain.gain.cancelScheduledValues(t);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.15);
      }
      setTimeout(() => {
        try { osc?.stop(); gain?.disconnect(); } catch { /* noop */ }
      }, 200);
    } catch { /* noop */ }
    ctxRef.current = null; oscRef.current = null; gainRef.current = null;
  };

  return { start, pulseAmp, rampFrequency, stop };
};

/* Short percussive tone (used for burst) */
const playTone = (freq = 880, duration = 260, type = 'sine', vol = 0.14) => {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration / 1000 + 0.02);
  } catch { /* noop */ }
};

/* Real firework sounds — rocket whistle, deep boom, then sparkling crackle. */
const noiseBuffer = (ctx, seconds) => {
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * seconds), ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  return buf;
};
const playRocket = (pan = 0) => {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const t = ctx.currentTime;
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(ctx, 0.5);
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 18;
    bp.frequency.setValueAtTime(900, t); bp.frequency.exponentialRampToValueAtTime(4200, t + 0.45);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.5, t + 0.08);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.48);
    const p = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
    if (p) p.pan.value = pan;
    src.connect(bp).connect(g); (p ? g.connect(p).connect(ctx.destination) : g.connect(ctx.destination));
    src.start(t); src.stop(t + 0.5);
  } catch { /* noop */ }
};
const playBoom = (pan = 0) => {
  try {
    const ctx = getAudioCtx(); if (!ctx) return;
    const t = ctx.currentTime;
    const out = ctx.createGain(); out.gain.value = 0.9;
    const p = ctx.createStereoPanner ? ctx.createStereoPanner() : null;
    if (p) { p.pan.value = pan; out.connect(p).connect(ctx.destination); } else out.connect(ctx.destination);
    // deep blast
    const src = ctx.createBufferSource(); src.buffer = noiseBuffer(ctx, 1.4);
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass';
    lp.frequency.setValueAtTime(2400, t); lp.frequency.exponentialRampToValueAtTime(140, t + 1.2);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.9, t + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.3);
    src.connect(lp).connect(g).connect(out); src.start(t); src.stop(t + 1.4);
    // thump
    const o = ctx.createOscillator(); const og = ctx.createGain();
    o.frequency.setValueAtTime(110, t); o.frequency.exponentialRampToValueAtTime(38, t + 0.35);
    og.gain.setValueAtTime(0.7, t); og.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
    o.connect(og).connect(out); o.start(t); o.stop(t + 0.42);
    // crackle sparkle tail
    for (let i = 0; i < 26; i++) {
      const at = t + 0.18 + Math.random() * 0.9;
      const c = ctx.createBufferSource(); c.buffer = noiseBuffer(ctx, 0.03);
      const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 2500 + Math.random() * 3000;
      const cg = ctx.createGain();
      cg.gain.setValueAtTime(0.0001, at); cg.gain.exponentialRampToValueAtTime(0.25 + Math.random() * 0.25, at + 0.003);
      cg.gain.exponentialRampToValueAtTime(0.0001, at + 0.03);
      c.connect(hp).connect(cg).connect(out); c.start(at); c.stop(at + 0.04);
    }
  } catch { /* noop */ }
};

const vibrate = (ms) => { try { nativeVibrate(ms); } catch { /* noop */ } };

/* Bg lerp — calm → tense (crimson) → calm again for phase transitions */
const lerpHex = (a, b, t) => {
  const p = (h) => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
  const [ar,ag,ab] = p(a); const [br,bg,bb] = p(b);
  const r = Math.round(ar + (br-ar)*t);
  const g = Math.round(ag + (bg-ag)*t);
  const bl = Math.round(ab + (bb-ab)*t);
  return `rgb(${r}, ${g}, ${bl})`;
};

/* ────────────── Rainbow burst field ────────────── */
const BurstField = ({ particles }) => (
  <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 15 }}>
    {particles.map((p) => (
      <span key={p.id}
        style={{
          position: 'absolute',
          left: `${p.x}%`,
          top: `${p.y}%`,
          width: p.size,
          height: p.size,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${p.color} 0%, ${p.color}CC 40%, transparent 100%)`,
          boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
          transform: `translate(-50%, -50%)`,
          '--dx': `${p.dx}vw`,
          '--dy': `${p.dy}vh`,
          animation: `relief-burst ${p.duration}ms cubic-bezier(0.16, 1, 0.3, 1) forwards`,
          animationDelay: `${p.delay}ms`,
          willChange: 'transform, opacity',
        }}
      />
    ))}
  </div>
);

/* ────────────── COMPONENT ────────────── */
export const TheRelief = ({ onClose }) => {
  useHistoryClose(true, onClose);
  const [phase, setPhase] = useState('intro'); // intro | breath1 | write | breath2 | burst | done
  const [text, setText] = useState('');
  const [cycleIdx, setCycleIdx] = useState(0);
  const [inhaling, setInhaling] = useState(true);
  const [particles, setParticles] = useState([]);
  const [burstIdx, setBurstIdx] = useState(-1);
  const audio = useBreathAudio();
  const timerRef = useRef([]);
  const { user } = useAuth();
  const step = Math.min(SPECTRUM.length - 1, cycleIdx * 2 + (inhaling ? 0 : 1));
  const beam = SPECTRUM[step];

  const scheduleTimeout = (fn, ms) => {
    const id = setTimeout(fn, ms);
    timerRef.current.push(id);
    return id;
  };
  const clearAllTimers = () => {
    timerRef.current.forEach(clearTimeout);
    timerRef.current = [];
  };

  const runBreathPhase = useCallback((next, freqStart, freqEnd) => {
    // Continuous sine — start at freqStart, ramp to freqEnd across the phase
    audio.start(freqStart);
    audio.rampFrequency(freqEnd, BREATH_PHASE_MS / 1000);
    let idx = 0;
    let ins = true;
    setCycleIdx(0);
    setInhaling(true);
    audio.pulseAmp(true);
    vibrate(30);

    const half = () => {
      ins = !ins;
      // 7th exhale finished → phase complete (counter never shows 8/7)
      if (ins && idx + 1 >= CYCLES_PER_PHASE) {
        audio.stop();
        scheduleTimeout(next, 200);
        return;
      }
      setInhaling(ins);
      audio.pulseAmp(ins);
      if (ins) {
        idx += 1;
        setCycleIdx(idx);
        vibrate(30);
      }
      scheduleTimeout(half, ins ? BREATH_IN_MS : BREATH_OUT_MS);
    };
    scheduleTimeout(half, BREATH_IN_MS);
  }, [audio]);

  /* Phase transitions */
  const startRitual = () => {
    setPhase('breath1');
  };

  useEffect(() => {
    if (phase === 'breath1') {
      runBreathPhase(() => setPhase('write'), 96, 128);      // low sine → gentle rise
    } else if (phase === 'breath2') {
      runBreathPhase(() => setPhase('burst'), 220, 528);     // harmonic uplift (A3 → C5 area)
    } else if (phase === 'burst') {
      triggerBurst();
    }
    return clearAllTimers;
  }, [phase]);

  /* On write → click Relief to release into orb + start final breath */
  const commitRelief = () => {
    if (phase !== 'write') return;
    vibrate(40);
    playTone(660, 220, 'sine');
    setPhase('breath2');
  };

  /* ───── PHASE 4 · Spectrum burst (implosion → shatter → radiance) */
  const triggerBurst = () => {
    try { sessionStorage.clear(); } catch { /* noop */ }
    audio.stop();
    BURST_ORIGINS.forEach(([ox, oy], n) => {
      const pan = (ox - 50) / 40;
      scheduleTimeout(() => playRocket(pan), n * BURST_GAP_MS);
      scheduleTimeout(() => {
        playBoom(pan);
        vibrate([40, 30, 60]);
        const arr = [];
        for (let i = 0; i < BURST_PARTICLES; i++) {
          const angle = Math.random() * Math.PI * 2;
          const dist = 35 + Math.random() * 60;
          arr.push({
            id: `${n}-${i}`,
            x: ox + (Math.random() - 0.5) * 6,
            y: oy + (Math.random() - 0.5) * 6,
            size: 6 + Math.random() * 9,
            color: BURST_PALETTE[(i + n * 3) % BURST_PALETTE.length],
            dx: Math.cos(angle) * dist, dy: Math.sin(angle) * dist,
            duration: 1500 + Math.random() * 800,
            delay: Math.random() * 120,
          });
        }
        setParticles((prev) => [...prev.filter((p) => !p.id.startsWith(`${n - 3}-`)), ...arr]);
        setBurstIdx(n);
      }, n * BURST_GAP_MS + ROCKET_MS);
    });
    scheduleTimeout(() => {
      setParticles([]);
      setBurstIdx(-1);
      setText('');
      setPhase('done');
    }, BURST_ORIGINS.length * BURST_GAP_MS + ROCKET_MS + 1400);
  };

  const restart = () => {
    clearAllTimers();
    audio.stop();
    setParticles([]);
    setBurstIdx(-1);
    setText('');
    setCycleIdx(0);
    setInhaling(true);
    setPhase('intro');
  };

  /* Phase III chime — one soft note each time the spectrum colour moves */
  useEffect(() => {
    if (phase === 'breath2') playTone(SPECTRUM_NOTES[step], 700, 'sine', 0.05);
  }, [phase, step]);

  /* Clean up on unmount */
  useEffect(() => () => { clearAllTimers(); audio.stop(); }, []);

  /* Reactive background — indigo aura on write, calm elsewhere */
  const bg = useMemo(() => {
    if (phase === 'write') {
      // Deepen indigo aura with text length
      const t = Math.min(1, text.length / 200);
      return lerpHex(BG_CALM, INDIGO_DEEP, 0.45 + 0.55 * t);
    }
    if (phase === 'breath2') {
      const progress = (cycleIdx + (inhaling ? 0 : 0.5)) / CYCLES_PER_PHASE;
      return lerpHex(INDIGO_DEEP, BG_CALM, progress);
    }
    return BG_CALM;
  }, [phase, text, cycleIdx, inhaling]);

  /* Ring + orb colors — indigo, with subtle gold accent retained */
  const ringColor = INDIGO_GLOW;
  const ringScale = inhaling ? 1.0 : 1.2;  // inhale shrinks, exhale expands
  const breathLabel = inhaling ? 'INHALE' : 'EXHALE';
  const phaseDurationSec = inhaling ? BREATH_IN_MS / 1000 : BREATH_OUT_MS / 1000;

  return (
    <div className="fixed inset-0 z-[220] flex flex-col overflow-hidden"
      data-testid="relief-modal"
      style={{
        background: bg,
        transition: 'background 700ms cubic-bezier(0.22, 0.61, 0.36, 1)',
      }}>
      <style>{`
        @keyframes relief-burst {
          0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0.2; }
          15%  { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
          100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) scale(0.35); opacity: 0; }
        }
        @keyframes relief-fade-up {
          0% { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes relief-orb-swirl {
          0% { transform: rotate(0); }
          100% { transform: rotate(360deg); }
        }
        @keyframes relief-orb-shudder {
          0%,100% { opacity: 1; }
          50%     { opacity: 0.9; }
        }
        @keyframes relief-halo {
          0%,100% { box-shadow: 0 0 40px var(--beam, ${INDIGO_GLOW}); opacity: 0.55; }
          50%     { box-shadow: 0 0 80px var(--beam, ${INDIGO_GLOW}); opacity: 0.95; }
        }
        .rc-fill-white { clip-path: inset(100% 0 0 0); animation: rc-fill 1.4s cubic-bezier(0.45,0,0.55,1) 0.6s forwards; }
        .rc-fill-gold { clip-path: inset(100% 0 0 0); animation: rc-fill 1.4s cubic-bezier(0.45,0,0.55,1) 2.1s forwards; }
        .rc-glow { animation: rc-glow-in 3.5s ease forwards, rc-gold-glow 2.4s ease-in-out 3.5s infinite; }
        @keyframes rc-glow-in { 0%,17% { box-shadow: 0 0 0 transparent; } 57% { box-shadow: 0 0 30px rgba(255,255,255,0.75); } 100% { box-shadow: 0 0 30px ${GOLD}AA; } }
        @keyframes rc-fill { to { clip-path: inset(0 0 0 0); } }
        @keyframes rc-gold-glow { 0%,100% { box-shadow: 0 0 34px ${GOLD}AA; } 50% { box-shadow: 0 0 56px ${GOLD_BRIGHT}, 0 0 90px ${GOLD}77; } }
        .rc-aura { animation: rc-aura 3.5s ease forwards; }
        @keyframes rc-aura { 0%,40% { filter: brightness(0.55) grayscale(0.4); } 100% { filter: brightness(1) grayscale(0); } }
        .rc-bright { opacity: 0; animation: rc-bright 1.4s ease 3.5s forwards; }
        @keyframes rc-bright { to { opacity: 1; } }
        .relief-word { animation: relief-word 1000ms ease forwards; }
        @keyframes relief-word { 0% { opacity: 0; transform: scale(0.85); } 15%,78% { opacity: 1; transform: scale(1); } 100% { opacity: 0; transform: scale(1.08); } }
        .relief-word-neg { animation: relief-word-neg 1000ms ease forwards; }
        @keyframes relief-word-neg { 0%,25% { opacity: 1; filter: blur(0); letter-spacing: 0.24em; } 70%,100% { opacity: 0; filter: blur(3px); letter-spacing: 0.4em; } }
        @keyframes relief-beam-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>

      {/* Header (sits ABOVE the gold frame) */}
      <div className="flex items-center justify-between px-3 py-2.5 flex-shrink-0 border-b relative z-[20]"
        style={{ borderColor: `${GOLD}22` }}>
        <BackPill onClick={onClose} accent="#E9C77A" testId="relief-close-btn"/>
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: '0 0 10px rgba(197,160,89,0.35)' }}>
          RELIEF · 87s RESET
        </p>
        <span className="w-14"/>
      </div>

      {/* ══════════ GOLD FRAME — brightening rectangular window that
           starts BELOW the header and reserves safe-area at the
           bottom so its lower edge never hides under the system nav. */}
      <div
        className="flex-1 min-h-0 flex flex-col relative m-2 rounded-lg overflow-hidden"
        style={{
          border: `2px solid ${GOLD_BRIGHT}`,
          transition: 'border-color 900ms ease, box-shadow 900ms ease',
          marginBottom: 'calc(max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 20px) + 8px)',
          boxShadow: `
            inset 0 0 0 1px rgba(255,220,150,0.35),
            inset 0 0 26px ${GOLD_BRIGHT}88,
            inset 0 0 60px ${GOLD}55,
            0 0 22px ${GOLD_BRIGHT}55,
            0 0 44px ${GOLD}33
          `,
        }}
        data-testid="relief-gold-frame"
      >

      {/* ═══════════ INTRO ═══════════ */}
      {phase === 'intro' && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-[10] text-center">
          <div className="rounded-full flex items-center justify-center"
            style={{ width: 132, height: 132, border: `1.5px solid ${GOLD}`,
              background: `radial-gradient(circle, ${INDIGO_GLOW}44, rgba(5,5,5,0.9))`,
              animation: 'relief-halo 3s ease-in-out infinite' }}>
            <span className="font-['Cinzel']" style={{ color: PEACH, fontSize: 44,
              textShadow: `0 0 20px ${INDIGO_GLOW}` }}>87<span style={{ fontSize: 18, marginLeft: 3 }}>s</span></span>
          </div>
          <p className="mt-6 font-['Cinzel'] uppercase tracking-[0.28em]"
            style={{ color: GOLD_BRIGHT, fontSize: 14 }}>Imperial Reset</p>
          <p className="mt-3 font-['Cormorant_Garamond'] italic max-w-xs"
            style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.55 }}>
            Four movements. Breath. Purge. Release. Burst.
            <br/>Return to yourself, lighter than before.
          </p>
          <button onClick={startRitual}
            data-testid="relief-begin-btn"
            className="mt-8 rounded-full px-7 py-3 font-['Cinzel'] uppercase tracking-[0.3em]"
            style={{ background: `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`,
              color: '#0a0a0a', fontSize: 12, border: `1px solid ${GOLD}`,
              boxShadow: `0 0 22px ${GOLD}88` }}>
            ✦ Begin Ritual
          </button>
        </div>
      )}

      {/* ═══════════ PHASE 1 · BREATH (Preparatory — centred layout) ═══════════ */}
      {phase === 'breath1' && (
        <div className="flex-1 flex flex-col items-center justify-center px-6 relative z-[10]"
          data-testid="relief-phase-breath1">
          <p className="font-['Outfit'] uppercase tracking-[0.32em]"
            style={{ color: PEACH, fontSize: 9 }}>
            Phase I · Preparatory
          </p>
          <p className="mt-2 font-['Cormorant_Garamond'] italic text-center"
            style={{ color: LAVENDER, fontSize: 13 }}>
            Follow the ring. Center your breath.
          </p>

          <div className="mt-8 relative flex items-center justify-center"
            style={{ width: 240, height: 240 }}>
            <div className="absolute rounded-full"
              data-testid="relief-breath-orb"
              style={{
                width: 130, height: 130,
                background: `radial-gradient(circle at 40% 40%,
                  ${INDIGO_GLOW} 0%, ${INDIGO} 55%, ${INDIGO_DEEP} 100%)`,
                boxShadow: `0 0 46px ${INDIGO_GLOW}99, inset 0 0 22px ${INDIGO_DEEP}`,
                border: `1px solid ${GOLD}44`,
                transform: `scale(${ringScale})`,
                transition: `transform ${phaseDurationSec}s cubic-bezier(0.45, 0, 0.55, 1)`,
                animation: 'relief-orb-swirl 6s linear infinite, relief-orb-shudder 900ms ease-in-out infinite',
              }}/>
            <div className="rounded-full"
              data-testid="relief-breath-ring"
              style={{
                width: 200, height: 200,
                border: `2px solid ${ringColor}`,
                background: `radial-gradient(circle, ${ringColor}22 0%, transparent 70%)`,
                boxShadow: `0 0 40px ${ringColor}66, inset 0 0 30px ${ringColor}22`,
                transform: `scale(${ringScale})`,
                transition: `transform ${phaseDurationSec}s cubic-bezier(0.45, 0, 0.55, 1)`,
              }}/>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="font-['Cinzel'] uppercase"
                data-testid="relief-breath-label"
                style={{ color: PEACH, fontSize: 20, letterSpacing: '0.32em',
                  textShadow: `0 0 12px ${INDIGO_GLOW}` }}>{breathLabel}</span>
              <span className="mt-2 font-['Outfit'] uppercase tracking-[0.28em]"
                style={{ color: `${LAVENDER}CC`, fontSize: 9 }}>
                Cycle {cycleIdx + 1} / {CYCLES_PER_PHASE}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ PHASE 3 · RELEASE (40% forged-text orb  |  60% breath ring) ═══════════ */}
      {phase === 'breath2' && (
        <div className="flex-1 flex flex-col min-h-0 relative z-[10]"
          data-testid="relief-phase-breath2">
          {/* Header labels */}
          <div className="flex-shrink-0 pt-3 text-center">
            <p className="font-['Outfit'] uppercase tracking-[0.32em]"
              style={{ color: PEACH, fontSize: 9 }}>
              Phase III · Release
            </p>
            <p className="mt-1 font-['Cormorant_Garamond'] italic"
              style={{ color: LAVENDER, fontSize: 12 }}>
              Transmuting the burden into light.
            </p>
          </div>

          {/* ─── TOP 40% — forged-text ball with orbiting energy beams ─── */}
          <div
            className="relative flex items-center justify-center overflow-hidden"
            style={{ flex: '0 0 40%', minHeight: 0 }}
            data-testid="relief-forged-orb-region"
          >
            {/* Accelerating energy-beam rings — rotate faster as each of
               the 7 breath cycles completes.  At cycle 0 the outer ring
               takes 9 s; by cycle 6 it whips around in ~1.8 s (peak spin
               just before burst). */}
            {(() => {
              const p = cycleIdx / (CYCLES_PER_PHASE - 1); // 0 → 1
              const outerSec = (9 - 7.2 * p).toFixed(2); // 9 → 1.8
              const innerSec = (6 - 4.8 * p).toFixed(2); // 6 → 1.2
              return (
                <>
                  <div
                    className="absolute rounded-full"
                    style={{
                      width: 220, height: 220,
                      border: `2px dashed ${beam}`,
                      boxShadow: `0 0 30px ${beam}AA, inset 0 0 20px ${beam}66`,
                      animation: `relief-beam-spin ${outerSec}s linear infinite`,
                      transition: 'border-color 900ms ease, box-shadow 900ms ease',
                    }}
                  />
                  <div
                    className="absolute rounded-full"
                    style={{
                      width: 180, height: 180,
                      border: `1.5px solid ${beam}CC`,
                      boxShadow: `0 0 20px ${beam}77, inset 0 0 14px ${beam}44`,
                      animation: `relief-beam-spin ${innerSec}s linear infinite reverse`,
                      transition: 'border-color 900ms ease, box-shadow 900ms ease',
                    }}
                  />
                </>
              );
            })()}
            {/* Radiating pulse aura */}
            <div
              className="absolute rounded-full"
              style={{
                width: 260, height: 260,
                background: `radial-gradient(circle, ${beam}33 0%, transparent 60%)`,
                animation: 'relief-halo 2.4s ease-in-out infinite',
                '--beam': beam, transition: 'background 900ms ease',
              }}
            />
            {/* Forged text ball */}
            <div
              className="relative flex items-center justify-center rounded-full px-4 py-3 text-center"
              style={{
                width: 140, height: 140,
                background: `radial-gradient(circle at 40% 40%, ${INDIGO_GLOW}CC 0%, ${INDIGO} 60%, ${INDIGO_DEEP} 100%)`,
                transition: 'background 900ms ease, box-shadow 900ms ease',
                boxShadow: `0 0 40px ${INDIGO_GLOW}AA, inset 0 0 20px #000`,
                border: `1.5px solid ${GOLD}`,
              }}
              data-testid="relief-forged-text-ball"
            >
              <span
                className="font-['Cormorant_Garamond'] italic block overflow-hidden"
                style={{
                  color: PEACH,
                  fontSize: 11,
                  lineHeight: 1.35,
                  maxHeight: 108,
                  textShadow: `0 0 8px ${INDIGO_GLOW}AA`,
                  wordBreak: 'break-word',
                }}
              >
                {text ? (text.length > 90 ? text.slice(0, 90) + '…' : text) : 'The burden inside you.'}
              </span>
            </div>
          </div>

          {/* ─── BOTTOM 60% — breath ring ─── */}
          <div
            className="flex-1 min-h-0 flex items-center justify-center relative"
            data-testid="relief-breath2-ring-region"
          >
            <div className="relative flex items-center justify-center"
              style={{ width: 240, height: 240 }}>
              <div className="absolute rounded-full"
                data-testid="relief-stress-orb"
                style={{
                  width: 130, height: 130,
                  background: `radial-gradient(circle at 40% 40%,
                    ${INDIGO_GLOW} 0%, ${INDIGO} 55%, ${INDIGO_DEEP} 100%)`,
                  boxShadow: `0 0 46px ${INDIGO_GLOW}99, inset 0 0 22px #000`,
                  border: `1px solid ${GOLD}44`,
                  transform: `scale(${ringScale})`,
                  transition: `transform ${phaseDurationSec}s cubic-bezier(0.45, 0, 0.55, 1), background 900ms ease, border-color 900ms ease, box-shadow 900ms ease`,
                  animation: 'relief-orb-swirl 6s linear infinite, relief-orb-shudder 900ms ease-in-out infinite',
                }}/>
              <div className="rounded-full"
                style={{
                  width: 200, height: 200,
                  border: `2px solid ${INDIGO_GLOW}`,
                  background: `radial-gradient(circle, ${INDIGO_GLOW}22 0%, transparent 70%)`,
                  boxShadow: `0 0 40px ${INDIGO_GLOW}66, inset 0 0 30px ${INDIGO_GLOW}22`,
                  transform: `scale(${ringScale})`,
                  transition: `transform ${phaseDurationSec}s cubic-bezier(0.45, 0, 0.55, 1), background 900ms ease, border-color 900ms ease, box-shadow 900ms ease`,
                }}/>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="font-['Cinzel'] uppercase"
                  data-testid="relief-breath-label"
                  style={{ color: PEACH, fontSize: 20, letterSpacing: '0.32em',
                    textShadow: `0 0 12px ${INDIGO_GLOW}` }}>{breathLabel}</span>
                <span className="mt-2 font-['Outfit'] uppercase tracking-[0.28em]"
                  style={{ color: `${LAVENDER}CC`, fontSize: 9 }}>
                  Cycle {cycleIdx + 1} / {CYCLES_PER_PHASE}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ PHASE 2 · WRITE ═══════════ */}
      {phase === 'write' && (
        <div className="flex-1 flex flex-col min-h-0 px-5 py-4 relative z-[10]"
          data-testid="relief-phase-write"
          style={{
            // Lift the Seal button above the on-screen keyboard while
            // the user is typing — `kbHeight` is set by the
            // VisualViewport listener above.  Falls back to the
            // safe-area-inset floor when the keyboard is closed.
            paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 40px)',
            transition: 'padding-bottom 220ms ease',
          }}>
          <p className="font-['Outfit'] uppercase tracking-[0.32em] text-center flex-shrink-0"
            style={{ color: PEACH, fontSize: 10 }}>Phase II · Purge</p>
          <p className="mt-1 font-['Cormorant_Garamond'] italic text-center flex-shrink-0"
            style={{ color: LAVENDER, fontSize: 14 }}>
            Write the heavy on you.
          </p>

          <div className="mt-3 flex-1 flex flex-col min-h-0 rounded-2xl relative overflow-hidden"
            style={{
              background: `radial-gradient(circle at 50% 0%, ${INDIGO}22 0%, rgba(255,255,255,0.03) 60%)`,
              border: `1px solid ${GOLD}`,
              boxShadow: `inset 0 0 40px ${INDIGO}33, 0 0 26px ${INDIGO_GLOW}55`,
              backdropFilter: 'blur(14px)',
              WebkitBackdropFilter: 'blur(14px)',
            }}>
            <textarea
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={600}
              placeholder="Everything that sits heavy today…"
              data-testid="relief-textarea"
              className="flex-1 min-h-0 w-full bg-transparent resize-none outline-none px-4 py-4 font-['Cormorant_Garamond'] overflow-y-auto"
              style={{
                color: PEACH, fontSize: 17, lineHeight: 1.55, fontStyle: 'italic',
                caretColor: LAVENDER,
              }}/>
            <div className="absolute bottom-2 right-3 font-['Outfit'] uppercase tracking-[0.24em] pointer-events-none px-1.5 rounded"
              style={{
                color: text.length > 200 ? '#E85A5A' : `${GOLD}88`,
                fontSize: 8,
                background: 'rgba(10,7,25,0.55)',
              }}>
              {text.length} / 600
            </div>
          </div>

          <button onClick={commitRelief}
            data-testid="relief-commit-btn"
            className="mt-5 rounded-full px-6 py-3 font-['Cinzel'] uppercase tracking-[0.3em] flex-shrink-0"
            style={{
              background: `linear-gradient(135deg, ${INDIGO_GLOW}, ${INDIGO})`,
              color: PEACH, fontSize: 12,
              border: `1px solid ${GOLD}`,
              boxShadow: `0 0 20px ${INDIGO_GLOW}88`,
            }}>
            ✦ Energy Out
          </button>
        </div>
      )}

      {/* ═══════════ PHASE 4 · BURST ═══════════ */}
      {phase === 'burst' && (
        <>
          <BurstField particles={particles}/>
          <div className="flex-1 relative z-[30] text-center pointer-events-none overflow-hidden"
            data-testid="relief-phase-burst">
            {burstIdx >= 0 && (
              <div key={burstIdx} className="absolute" data-testid={`relief-burst-word-${burstIdx}`}
                style={{ left: `${Math.min(68, Math.max(32, BURST_ORIGINS[burstIdx][0]))}%`,
                  top: `${Math.min(82, Math.max(16, BURST_ORIGINS[burstIdx][1]))}%`,
                  transform: 'translate(-50%, -50%)', width: '62%' }}>
              <div className="relief-word">
                <p className="font-['Cinzel'] uppercase relief-word-neg"
                  style={{ color: 'rgba(220,210,240,0.95)', fontSize: 12, letterSpacing: '0.24em', whiteSpace: 'nowrap', textShadow: '0 2px 6px #000' }}>
                  {BURST_WORDS[burstIdx][0]}
                </p>
                <p className="font-['Cormorant_Garamond'] italic mt-1"
                  style={{ color: GOLD_BRIGHT, fontSize: 28, lineHeight: 1.15, whiteSpace: 'nowrap',
                    textShadow: `0 2px 6px #000, 0 0 18px ${GOLD_BRIGHT}, 0 0 40px ${GOLD}` }}>
                  {BURST_WORDS[burstIdx][1]}
                </p>
              </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ═══════════ DONE · AFFIRMATION ═══════════ */}
      {phase === 'done' && (
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center gap-10 px-6 py-8 relative z-[10] text-center"
          data-testid="relief-done">
          <div className="absolute inset-0 pointer-events-none rc-bright"
            style={{ background: `radial-gradient(ellipse at 50% 40%, ${GOLD_BRIGHT}55 0%, ${GOLD}22 45%, transparent 75%)` }}/>
          <ReliefCrest user={user}/>
          <p className="relative font-['Cormorant_Garamond'] italic text-center max-w-sm flex-shrink-0"
            data-testid="relief-affirmation"
            style={{ color: PEACH, fontSize: 20, lineHeight: 1.45,
              textShadow: `0 0 14px ${PEACH}77, 0 0 24px ${INDIGO_GLOW}66`,
              animation: 'relief-fade-up 1000ms cubic-bezier(0.22, 0.61, 0.36, 1) 4200ms both' }}>
            {AFFIRMATION}
          </p>
          <div className="relative flex gap-3 flex-shrink-0"
            style={{ animation: 'relief-fade-up 900ms ease 5000ms both' }}>
            <button onClick={restart}
              data-testid="relief-again-btn"
              className="rounded-full px-5 py-2.5 font-['Cinzel'] uppercase tracking-[0.24em]"
              style={{ background: 'rgba(197,160,89,0.10)', color: GOLD_BRIGHT,
                border: `1px solid ${GOLD}`, fontSize: 10 }}>
              ↻ Reset Again
            </button>
            <button onClick={onClose}
              data-testid="relief-done-btn"
              className="rounded-full px-5 py-2.5 font-['Cinzel'] uppercase tracking-[0.28em]"
              style={{ background: `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`,
                color: '#0a0a0a', border: `1px solid ${GOLD}`, fontSize: 10,
                boxShadow: `0 0 18px ${GOLD}66` }}>
              Sealed
            </button>
          </div>
        </div>
      )}
      </div>{/* /gold-frame */}
    </div>
  );
};

/* Tile icon — procedural phoenix crest, no images */
export const ReliefTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(197,160,89,0.6))' }}>
    <defs>
      <linearGradient id="rlfIcon" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#F0DAA3"/>
        <stop offset="0.6" stopColor="#C5A059"/>
        <stop offset="1" stopColor="#7A5722"/>
      </linearGradient>
    </defs>
    <path d="M12 11 C 8 6, 4 7, 2 12 C 6 10, 9 11, 12 14 M 12 11 C 16 6, 20 7, 22 12 C 18 10, 15 11, 12 14"
      fill="none" stroke="url(#rlfIcon)" strokeWidth="1.4" strokeLinecap="round"/>
    <path d="M12 8 C 10.5 8, 10 10, 10 11.5 C 10 13, 11 15, 12 15 C 13 15, 14 13, 14 11.5 C 14 10, 13.5 8, 12 8 Z"
      fill="url(#rlfIcon)" stroke="#0a0a0a" strokeWidth="0.3"/>
    <path d="M12 15 L 10 21 L 12 18 L 14 21 Z" fill="url(#rlfIcon)"/>
  </svg>
);

export default TheRelief;
